import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { BottomSheet, Column, Menu, Popover, Sidebar, TextButton, Typography } from 'seagull';
import { Checkmark, ChevronDown, ChevronRight, PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useCreateVendorAccount } from '~/hooks/useCreateVendorAccount/useCreateVendorAccount';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useScrollBorder } from '~/hooks/useScrollBorder';
import { prevBusinessIdKey, useSwitchBusiness } from '~/hooks/useSwitchBusiness';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import styles from './BusinessSwitcher.module.scss';
import { getBusinessTriggerLogoUrl, getBusinessTriggerSubtext } from './BusinessSwitcher.utils';
import { BusinessSwitcherLogo } from './BusinessSwitcherLogo';
export const getBusinessSwitcherTID = ()=>getTestIds();
export const BusinessSwitcher = (param)=>{
    let { userInfo, inSidebar } = param;
    var _anchorEl_current;
    const { BusinessSwitcherTID } = useMemo(()=>getBusinessSwitcherTID(), []);
    const { logBI, resetBusinessData, setWorksAt } = useStoreActions();
    const { switchBusiness } = useSwitchBusiness();
    const { localStorage } = useIoContext();
    const workspaces = userInfo.workspaces() || [];
    const businessId = userInfo.businessId();
    const isClient = useStore(selectors.user.isClient);
    const onCreateVendorAccount = useCreateVendorAccount(()=>logBI(18107));
    const { t: bsTexts } = useLocaleKeys('components.navigation.businessSwitcher');
    const [open, setOpen] = useState(false);
    const anchorEl = useRef(null);
    const menuListRef = useRef(null);
    const { SP_ONBOARDING_PHASE_1 } = useFeatureFlags();
    const history = useHistory();
    const validWorkspaces = workspaces.filter((workspace)=>workspace.isDisplayableInSwitcher());
    const hasAVendorAccount = userInfo.hasAVendorAccount();
    const { footerClassName } = useScrollBorder({
        contentRef: menuListRef,
        enabled: open,
        footerBaseClassName: styles.footerScrollBorder
    }, [
        open,
        validWorkspaces
    ]);
    const menuItems = useMemo(()=>{
        return validWorkspaces.map((workspace, index)=>{
            const selected = workspace.id() === businessId;
            const menuItem = {
                id: workspace.id(),
                body: workspace.name(),
                subtext: workspace.getBusinessSwitcherItemSubtext(bsTexts),
                selected,
                suffixIcon: selected ? /*#__PURE__*/ _jsx(Checkmark, {}) : undefined,
                testId: BusinessSwitcherTID.Item(index),
                prefixIcon: /*#__PURE__*/ _jsx(BusinessSwitcherLogo, {
                    testId: BusinessSwitcherTID.MenuItem(index),
                    src: workspace.getBusinessSwitcherItemLogoUrl()
                }),
                bi: {
                    name: 'business_switcher_select_workspace',
                    data: {
                        workspace_id: workspace.id()
                    }
                },
                onClick: ()=>{
                    if (selected) return;
                    setOpen(false);
                    switchBusiness(workspace.id());
                }
            };
            return menuItem;
        });
    }, [
        validWorkspaces,
        switchBusiness,
        BusinessSwitcherTID,
        businessId
    ]);
    const footerItems = useMemo(()=>{
        return compact([
            hasAVendorAccount && {
                variant: 'contextualAction',
                props: {
                    bi: {
                        name: 'business_switcher_create_additional_account'
                    },
                    testId: BusinessSwitcherTID.ContextualAction(),
                    title: bsTexts.createAdditionalAccount(),
                    onClick: ()=>{
                        setOpen(false);
                        logBI(11151);
                        if (businessId) {
                            localStorage.setItem(prevBusinessIdKey, businessId);
                        }
                        resetBusinessData();
                        setWorksAt();
                        if (SP_ONBOARDING_PHASE_1) {
                            history.push(routes.vendorOnboarding);
                        }
                    },
                    icon: /*#__PURE__*/ _jsx(PlusCircle, {})
                }
            },
            !hasAVendorAccount && {
                variant: 'promotionalAction',
                props: {
                    testId: BusinessSwitcherTID.PromotionalAction(),
                    title: bsTexts.tryAnchor(),
                    body: bsTexts.tryAnchorBody(),
                    onClick: ()=>{
                        setOpen(false);
                        onCreateVendorAccount();
                    },
                    className: {
                        wrapper: styles.promotionalActionWrapper,
                        button: styles.promotionalActionButton
                    },
                    bi: {
                        name: 'try_anchor_for_your_business'
                    }
                }
            }
        ]);
    }, [
        bsTexts,
        logBI,
        hasAVendorAccount,
        onCreateVendorAccount,
        BusinessSwitcherTID,
        resetBusinessData,
        setWorksAt,
        setOpen,
        businessId,
        localStorage,
        SP_ONBOARDING_PHASE_1,
        history
    ]);
    function toggleMenu() {
        const wasOpen = open;
        setOpen((prev)=>!prev);
        if (!wasOpen && isClient) {
            logBI(18106);
        }
    }
    const minWidth = (_anchorEl_current = anchorEl.current) === null || _anchorEl_current === void 0 ? void 0 : _anchorEl_current.offsetWidth;
    return inSidebar ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Sidebar.Item, {
                id: "business-switcher",
                onClick: toggleMenu,
                testId: BusinessSwitcherTID.Button(),
                bi: {
                    name: 'sidebar_business_switcher'
                },
                after: /*#__PURE__*/ _jsx(ChevronRight, {
                    size: 16
                }),
                children: userInfo.businessName()
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: open,
                onClose: ()=>setOpen(false),
                paddingBottom: 0,
                children: /*#__PURE__*/ _jsx(Menu, {
                    testId: BusinessSwitcherTID.Menu(),
                    dimensions: {
                        width: 'auto'
                    },
                    items: menuItems,
                    footer: {
                        items: footerItems
                    }
                })
            })
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TextButton, {
                onClick: toggleMenu,
                testId: BusinessSwitcherTID.Button(),
                bi: {
                    name: 'open_business_switcher'
                },
                prefixIcon: /*#__PURE__*/ _jsx(BusinessSwitcherLogo, {
                    fullLogo: true,
                    testId: BusinessSwitcherTID.Button(),
                    src: getBusinessTriggerLogoUrl(userInfo.business())
                }),
                suffixIcon: /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.arrowIcon, {
                        [styles.open]: open
                    }),
                    children: /*#__PURE__*/ _jsx(ChevronDown, {})
                }),
                skin: "secondary",
                underline: "none",
                ref: anchorEl,
                children: /*#__PURE__*/ _jsxs(Column, {
                    alignItems: "start",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            ellipsis: true,
                            children: userInfo.businessName()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            ellipsis: true,
                            children: getBusinessTriggerSubtext(userInfo.business(), bsTexts)
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Popover, {
                open: open,
                onClose: ()=>setOpen(false),
                anchorEl: anchorEl.current,
                children: /*#__PURE__*/ _jsx(Menu, {
                    testId: BusinessSwitcherTID.Menu(),
                    dimensions: {
                        width: 'fit-content',
                        minWidth
                    },
                    classes: {
                        menuList: styles.menuList
                    },
                    items: menuItems,
                    footer: {
                        items: footerItems,
                        className: footerClassName
                    },
                    menuListRef: menuListRef
                })
            })
        ]
    });
};
export function BusinessSwitcherForSpecificUsers(param) {
    let { inSidebar } = param;
    const userInfo = useStore((state)=>state.userInfo);
    const showBusinessSwitcher = useStore(selectors.showBusinessSwitcher);
    const isViewModeFromStore = useStore(selectors.isViewMode);
    const { ENABLE_KILL_ACCOUNTANT_ACCESS_FE } = useFeatureFlags();
    const isViewMode = !!ENABLE_KILL_ACCOUNTANT_ACCESS_FE && isViewModeFromStore;
    if (!userInfo || !showBusinessSwitcher || isViewMode) {
        return null;
    }
    return /*#__PURE__*/ _jsx(BusinessSwitcher, {
        userInfo: userInfo,
        inSidebar: inSidebar
    });
}
